import React, { useState, useEffect } from 'react';
import { Card, Button, Input, message, Space, Typography, Row, Col, Statistic } from 'antd';
import { SaveOutlined, ClearOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { getWhiteboard, saveWhiteboard } from '../services/whiteboardService';
import { themeConfig } from '../config/theme';

const { TextArea } = Input;
const { Title } = Typography;

const Whiteboard = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadWhiteboardContent();
  }, []);

  const loadWhiteboardContent = async () => {
    try {
      setLoading(true);
      const response = await getWhiteboard();
      if (response && response.content) {
        setContent(response.content);
        setLastSaved(response.lastUpdated);
      }
    } catch (error) {
      console.error('Error loading whiteboard content:', error);
      // Don't show error message if whiteboard doesn't exist yet
      if (error.response?.status !== 404) {
        messageApi.error('Failed to load whiteboard content');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveWhiteboardContent = async () => {
    try {
      setSaving(true);
      const response = await saveWhiteboard(content);
      
      setLastSaved(response.lastUpdated);
      messageApi.success('Whiteboard content saved successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving whiteboard content:', error);
      messageApi.error('Failed to save whiteboard content');
    } finally {
      setSaving(false);
    }
  };

  const clearContent = () => {
    setContent('');
    setIsEditing(true);
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const formatLastSaved = (timestamp) => {
    if (!timestamp) return 'Never saved';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div>
      {contextHolder}
      <Title level={2}>Whiteboard</Title>
      
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Statistic
            title="Status"
            value={isEditing ? "Editing" : "Saved"}
            prefix={<EditOutlined />}
            valueStyle={{ color: isEditing ? themeConfig.status.warning : themeConfig.status.successDark }}
          />
        </Col>
        <Col span={12}>
          <Statistic
            title="Last Saved"
            value={formatLastSaved(lastSaved)}
            prefix={<FileTextOutlined />}
          />
        </Col>
      </Row>

      <Card
        title="Notes & Ideas"
        extra={
          <Space>
            <Button
              icon={<ClearOutlined />}
              onClick={clearContent}
              disabled={!content}
            >
              Clear
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveWhiteboardContent}
              loading={saving}
              disabled={!isEditing}
            >
              Save
            </Button>
          </Space>
        }
        style={{ minHeight: '500px' }}
      >
        <TextArea
          value={content}
          onChange={handleContentChange}
          placeholder="Write your notes, ideas, reminders, or anything else here..."
          autoSize={{ minRows: 20, maxRows: 30 }}
          style={{ 
            border: 'none',
            boxShadow: 'none',
            fontSize: '14px',
            lineHeight: '1.6'
          }}
          loading={loading}
        />
      </Card>

      <div style={{ marginTop: 16, padding: 16, backgroundColor: themeConfig.background.tableHeader, borderRadius: 6 }}>
        <Title level={5}>Tips:</Title>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li>Use this space for meeting notes, reminders, ideas, or any other information</li>
          <li>Content is automatically saved when you click the Save button</li>
          <li>Your notes will persist across sessions</li>
          <li>Use the Clear button to start fresh</li>
        </ul>
      </div>
    </div>
  );
};

export default Whiteboard;
